import { Box, makeStyles } from '@material-ui/core';
import { useMemo, useState } from 'react';

import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { MainNav } from '@/components/shared/components/MainNav';
import { StyledAccordion } from '@/components/shared/components/StyledAccordion';
import { Text } from '@/components/shared/components/Text';
import { NavSection } from '@/config/types';
import { useConfigContext } from '@/contexts/ConfigContext/ConfigContext';
import { usePageContext } from '@/contexts/PageContext/PageContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';
import { useUIWrapper } from '@/hooks/useUIWrapper';

export const MainMenu = () => {
  const {
    state: { currentLayout },
  } = useConfigContext();
  const {
    state: { showLeftMenu },
  } = usePageContext();

  const { leftMenuWidth } = useUIWrapper();
  const { sm } = useScreen();
  const { background } = useCustomTheme();

  const initialExpandedSections = useMemo(
    () =>
      currentLayout?.navSections.reduce<Record<string, boolean>>((acc, section) => {
        if (section.nameTextId) acc[section.nameTextId] = true;
        return acc;
      }, {}),
    [currentLayout?.navSections],
  );

  const [expanded, setExpanded] = useState(initialExpandedSections);

  const useStyles = makeStyles({
    root: {
      scrollbarWidth: 'none',
      '&::WebkitScrollbar': {
        display: 'none',
      },
    },
  });

  const handleChange = (section: NavSection) => {
    const nameId = section.nameTextId;
    if (!nameId) return;

    setExpanded((prev) => ({
      ...prev,
      [nameId]: !prev?.[nameId],
    }));
  };

  const ExpandedIcon = () => <Icon>expand_more</Icon>;
  const accordionSummaryStyle = { maxHeight: '32px', padding: '0 4px' };

  const navSections = currentLayout?.navSections;

  const classes = useStyles();

  return (
    <Box
      style={
        {
          '&::after': {
            boxShadow: `0px 26px 4px -24px ${background} inset, 0px 30px 10px -24px ${background} inset, 0px -30px 5px -23px ${background} inset;`,
          },
        } as React.CSSProperties
      }
      className={showLeftMenu ? 'fade-vertical-wrapper' : 'fade-vertical-wrapper fade-vertical-wrapper-hide'}
    >
      <Box
        className={`${classes.root} `}
        style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto', overflowX: 'hidden', paddingBlock: '5px' }}
        maxWidth={sm ? `calc(${leftMenuWidth} + 50px)` : 'unset'}
      >
        {currentLayout && (
          <>
            {navSections?.map((section, i) =>
              section.nameTextId ? (
                <div key={section.nameTextId} style={{ marginBottom: 24, overflow: 'hidden' }}>
                  <StyledAccordion.Accordion
                    expanded={section.nameTextId ? expanded?.[section.nameTextId] : true}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleChange(section);
                    }}
                    style={{ backgroundColor: 'rgba(0,0,0,0)' }}
                  >
                    <StyledAccordion.AccordionSummary
                      expandIcon={ExpandedIcon()}
                      IconButtonProps={{ size: 'small' }}
                      style={accordionSummaryStyle}
                    >
                      {section.nameTextId && (
                        <>
                          <Font variant={'subtitle2'} color={'title'} style={{ display: 'block', marginBlock: '12px' }}>
                            <Text strong tId={section.nameTextId} />
                          </Font>
                        </>
                      )}
                    </StyledAccordion.AccordionSummary>
                    <StyledAccordion.AccordionDetails style={{ marginTop: 8 }}>
                      <Box display={'flex'} flexDirection={'column'} width={'100%'}>
                        {section.links.length &&
                          section.links.map((link, i) => (
                            <Box key={i}>
                              <MainNav
                                textId={currentLayout.pagesByPath[link.path]?.titleTextId}
                                linkTo={link.path}
                                icon={link.icon}
                              />
                            </Box>
                          ))}
                      </Box>
                    </StyledAccordion.AccordionDetails>
                  </StyledAccordion.Accordion>
                </div>
              ) : (
                <Box
                  key={`unnamed-section-${i}`}
                  display={'flex'}
                  flexDirection={'column'}
                  width={'100%'}
                  pt={2}
                  pb={3}
                >
                  {section.links.length &&
                    section.links.map((link, i) => (
                      <Box key={i}>
                        <MainNav
                          textId={currentLayout.pagesByPath[link.path]?.titleTextId}
                          linkTo={link.path}
                          icon={link.icon}
                        />
                      </Box>
                    ))}
                </Box>
              ),
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
